<template>
  <div class="geo-finder d-flex flex-column align-items-center px-2">
    <img
      :src="heroLogo"
      style="border: none; max-height: 125px"
    />
    <div class="card mt-3" style="width:100%">
      <div class="border-bottom title-part-padding">
        <h4 class="card-title mb-0">Share Your Location</h4>
      </div>
      <div class="card-body">
        <div
          v-if="isLoading"
          class="spinner-border text-center mt-3"
          role="status"
        >
          <span class="sr-only">Loading...</span>
        </div>
        <div v-else>
          <div v-if="error">
            {{ error }}
            <div class="mt-3" v-show="manualInput">
              <GmapAutocomplete
                :value="address"
                placeholder="Start typing your address"
                @place_changed="manualLocationUpdated"
                :select-first-on-enter="true"
                class="form-control"
              />
            </div>
          </div>
          <div v-else>
            <h6 v-if="address" class="pb-2">{{ address }}</h6>
            <GoogleMap
              style="height: 75vh"
              :geography="[{ lat, lng, ts: timestamp, recipient_id: magicToken.recipient_id }]"
              :recipientId="magicToken.recipient_id"
            />
            <small class="text-muted ml-2"
              ><em
                >last updated {{ getLastLocationTime(lastUpdated) }}</em
              ></small
            >
          </div>
        </div>
      </div>
    </div>
    <div class="geo-finder-chat">
      <CallerChat v-if="magicToken" :magicToken="magicToken" />
    </div>
  </div>
</template>

<script>
/* eslint-disable */
import GoogleMap from "./GoogleMap";
import CallerChat from "./CallerChat";
import moment from "moment-timezone";
import axios from "axios";

export default {
  components: {
    GoogleMap,
    CallerChat,
  },
  data() {
    return {
      lat: null,
      lng: null,
      accuracy: null,
      altitude: null,
      altitudeAccuracy: null,
      heading: null,
      timestamp: null,
      address: "",
      manualInput: true,
      lastUpdated: null,
      error: null,
      isLoading: false,
      geography: [],
      isPostedLocation: false,
    };
  },
  computed: {
    heroLogo: function() {
      if (this.magicToken && this.magicToken.token_data && this.magicToken.token_data.agency_logo)
        return this.magicToken.token_data.agency_logo;
        
      return "/images/Convey911_Logo_266x95.png";
    }
  },
  props: {
    magicToken: Object,
  },
  async created() {
    if (
      !this.magicToken ||
      (this.magicToken &&
        this.magicToken.service_type != "addRecipientGeolocation" && this.magicToken.service_type != "createVideoRoomToken")
    ) {
      this.error = "Token service type not supported";
      return;
    }

    this.isLoading = true;
    this.isPostedLocation = false;
    this.getLocation();
  },
  mounted() {
    this.watchLocation();
  },
  methods: {
    getLastLocationTime(t) {
      try {
        const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
        const formattedTime = moment.utc(t, "x").tz(tz).fromNow();
        return formattedTime;
      } catch (e) {
        return t;
      }
    },
    watchLocation() {
      const success = async (position) => {
        setTimeout(() => {
          this.lat = position.coords.latitude;
          this.lng = position.coords.longitude;
          this.timestamp = position.timestamp;

          this.isLoading = false;

          this.geography = [
            { lat: this.lat, lng: this.lng },
            ...this.geography,
          ];
          this.getAddressFrom(this.lat, this.lng);
          this.postLocation(position.coords);
        }, 15000);
      };
      const error = (err) => {
        console.log("Browser timeout error access geolocation", err);
        if (err.code === 1)
          this.error = "Location sharing is not enabled on your browser. Please enter your address manually.";
        else
          this.error = "Your browser location has timed out. Please refresh.";

        this.isLoading = false;        
      };
      const options = {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 0,
      };

      if (navigator.geolocation && !this.error) 
        navigator.geolocation.watchPosition(success, error, options);
      
    },
    getLocation() {
      if (navigator.geolocation) {
        this.manualInput = false;
        const options = {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 0,
        };        
        navigator.geolocation.getCurrentPosition(
          (position) => {
            this.lat = position.coords.latitude;
            this.lng = position.coords.longitude;
            this.timestamp = position.timestamp;

            this.isLoading = false;

            this.geography.push({ lat: this.lat, lng: this.lng });
            this.getAddressFrom(this.lat, this.lng);
            this.postLocation(position.coords);
          },
          (error) => {
            console.log(error);
            this.error = "Location settings are not enabled in your browser. Please enter your address below.";
            this.isLoading = false;
            this.manualInput = true;
          },
          options
        );
      } else {
        this.error = "Your browser does not support geolocation API. Please enter your address manually.";
        this.isLoading = false;
      }
    },
    async postLocation(coords) {
      this.lastUpdated = moment();

      let payload = {
        lat: coords.latitude,
        lng: coords.longitude
      };

      if (coords.accuracy)
        payload["latlng_accuracy"] = coords.accuracy;
      if (coords.altitude)
        payload["altitude"] = coords.altitude;
      if (coords.altitudeAccuracy)
        payload["altitude_accuracy"] = coords.altitudeAccuracy;

      if (this.magicToken.access_token.length > 50) {
        let recipientId = this.magicToken.recipient_id;
        if (!recipientId && this.magicToken.token_data.recipient_id) 
          recipientId = this.magicToken.token_data.recipient_id;
        
        if (recipientId === null)
          throw "Missing or invalid recipient ID";

        await this.$ConveyApi.recipientsApi.addRecipientGeolocation({ numberOrId: recipientId, payload });
        if (!this.isPostedLocation) {
          this.$emit("postedLocation");
        }
        this.isPostedLocation = true;
      } else {
        await this.$ConveyApi.tokensApi.fulfillMagicToken({
          token: this.magicToken.token,
          accessToken: this.magicToken.access_token,
          payload,
        });
        if (!this.isPostedLocation) {
          this.$emit("postedLocation");
        }
        this.isPostedLocation = true;
      }
    },
    manualLocationUpdated(addressData) { 
      this.lat = addressData.geometry.location.lat();
      this.lng = addressData.geometry.location.lng();
      this.address = addressData.formatted_address;
      this.postLocation({
        latitude: this.lat,
        longitude: this.lng,
        formattedAddress: this.address
      });
      this.error = null;
    },
    getAddressFrom(lat, long) {
      axios.get(
        `https://maps.googleapis.com/maps/api/geocode/json?latlng=${lat},${long}&key=${process.env.VUE_APP_GOOGLE_API_KEY}`
      )
        .then((response) => {
          if (response.data.error_message) {
            this.error = response.data.error_message;
            // console.log(response.data.error_message);
          } else {
            this.address = response.data.results[0].formatted_address;
          }
        })
        .catch((error) => {
          console.log(error.message);
        });
    },
  },
};
</script>

<style lang="scss" scoped>
.geo-finder {
  position: relative;
  &-chat {
    position: fixed;
    bottom: 1rem;
    right: 1rem;
  }
}
</style>